/**
 * VendorSearchSelect — searchable typeahead for vendor selection.
 *
 * Client-side filter over `/api/vendors` (returns ≤1000 rows). Same
 * "type to filter, forced pick from matches" guardrail as
 * StockItemSearch — the parent's `value` (vendor_id) can only be
 * committed via a click on a matched row; free-text is never accepted.
 *
 * Props:
 *   value       — currently-selected vendor_id (string)
 *   onSelect    — called with the full vendor row { id, name, ... }
 *                 or { id: '' } when the user clears the field.
 *   disabled    — locks the control (used in read-only PO detail).
 *   placeholder — input placeholder text
 *   testId      — data-testid prefix
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VendorSearchSelect({
  value,
  onSelect,
  disabled = false,
  placeholder = 'Type vendor name to search…',
  testId = 'vendor-search',
}) {
  const [vendors, setVendors] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const wrapperRef = useRef(null);

  // Load all vendors once — small dataset, client-side filter.
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/api/vendors`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => setVendors(Array.isArray(res.data) ? res.data : []))
      .catch(() => setVendors([]));
  }, []);

  // Keep label in sync with external value changes.
  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return;
    }
    const found = vendors.find((v) => v.id === value);
    if (found) setSelectedLabel(found.name || found.id);
  }, [value, vendors]);

  // Outside-click closes.
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return vendors.slice(0, 20);
    return vendors
      .filter(
        (v) =>
          (v.name || '').toLowerCase().includes(q) ||
          (v.id || '').toLowerCase().includes(q) ||
          (v.gstin || '').toLowerCase().includes(q),
      )
      .slice(0, 20);
  }, [query, vendors]);

  const pick = useCallback(
    (v) => {
      setSelectedLabel(v.name || v.id);
      setQuery('');
      setOpen(false);
      onSelect?.(v);
    },
    [onSelect],
  );

  const clear = () => {
    setSelectedLabel('');
    setQuery('');
    onSelect?.({ id: '' });
  };

  // When a vendor is committed, show the badge; otherwise show the input.
  if (selectedLabel && !open) {
    return (
      <div
        ref={wrapperRef}
        className="flex items-center gap-2 border rounded-md px-2 py-1.5 bg-zinc-50"
        data-testid={`${testId}-selected`}
      >
        <span className="text-sm flex-1 truncate">{selectedLabel}</span>
        {!disabled && (
          <button
            type="button"
            onClick={clear}
            className="text-zinc-400 hover:text-zinc-700"
            data-testid={`${testId}-clear`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={`${testId}-input`}
      />
      {open && !disabled && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-zinc-500">
              No matching vendors
            </div>
          )}
          {filtered.map((v) => (
            <div
              key={v.id}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(v);
              }}
              className="px-3 py-2 text-sm hover:bg-zinc-100 cursor-pointer border-b last:border-b-0"
              data-testid={`${testId}-option-${v.id}`}
            >
              <div className="font-medium">{v.name || v.id}</div>
              {v.gstin && (
                <div className="text-xs text-zinc-500 font-mono">
                  GSTIN: {v.gstin}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
